/**
 * Terrain (existing ground) surfaces built from drone survey data.
 *
 * A terrain is a north-up elevation grid in the same map coordinates as the
 * georeferenced image layer:
 *   { originX, originY, cellSize, cols, rows, data: Float32Array, units, fileName }
 * originX/originY are the map coordinates of the grid's top-left corner; row 0 is
 * the northern edge. Cells with no survey coverage hold NaN.
 */

const PLY_TYPES = {
  char: ['getInt8', 1], int8: ['getInt8', 1],
  uchar: ['getUint8', 1], uint8: ['getUint8', 1],
  short: ['getInt16', 2], int16: ['getInt16', 2],
  ushort: ['getUint16', 2], uint16: ['getUint16', 2],
  int: ['getInt32', 4], int32: ['getInt32', 4],
  uint: ['getUint32', 4], uint32: ['getUint32', 4],
  float: ['getFloat32', 4], float32: ['getFloat32', 4],
  double: ['getFloat64', 8], float64: ['getFloat64', 8],
};

/**
 * Parse a binary little-endian PLY mesh (as exported by 3Dsurvey).
 * Returns { positions: Float64Array (x,y,z triples), indices: Uint32Array (triangles) }.
 */
export const parsePly = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const headerEnd = findHeaderEnd(bytes);
  const header = new TextDecoder().decode(bytes.subarray(0, headerEnd)).split(/\r?\n/);
  if (header[0] !== 'ply') throw new Error('Not a PLY file');
  const format = header.find(l => l.startsWith('format '));
  if (!format?.includes('binary_little_endian')) {
    throw new Error('Only binary PLY files are supported - re-export from 3Dsurvey as binary');
  }

  const elements = [];
  for (const line of header) {
    const parts = line.trim().split(/\s+/);
    if (parts[0] === 'element') {
      elements.push({ name: parts[1], count: parseInt(parts[2], 10), properties: [] });
    } else if (parts[0] === 'property' && elements.length) {
      const props = elements[elements.length - 1].properties;
      if (parts[1] === 'list') props.push({ list: true, countType: parts[2], type: parts[3], name: parts[4] });
      else props.push({ type: parts[1], name: parts[2] });
    }
  }

  const view = new DataView(buffer);
  let offset = headerEnd;
  let positions = null;
  let indices = null;

  for (const element of elements) {
    if (element.name === 'vertex') {
      positions = new Float64Array(element.count * 3);
      const fixed = element.properties.every(p => !p.list);
      if (!fixed) throw new Error('Unsupported PLY vertex layout');
      const layout = element.properties.map(p => ({ name: p.name, read: PLY_TYPES[p.type][0], size: PLY_TYPES[p.type][1] }));
      const axis = { x: 0, y: 1, z: 2 };
      for (let i = 0; i < element.count; i++) {
        for (const prop of layout) {
          const a = axis[prop.name];
          if (a !== undefined) positions[i * 3 + a] = view[prop.read](offset, true);
          offset += prop.size;
        }
      }
    } else if (element.name === 'face') {
      const triangles = [];
      const faceProp = element.properties.find(p => p.list);
      const [countRead, countSize] = PLY_TYPES[faceProp.countType];
      const [indexRead, indexSize] = PLY_TYPES[faceProp.type];
      for (let i = 0; i < element.count; i++) {
        for (const prop of element.properties) {
          if (!prop.list) { offset += PLY_TYPES[prop.type][1]; continue; }
          const [cRead, cSize] = PLY_TYPES[prop.countType];
          const [iRead, iSize] = PLY_TYPES[prop.type];
          const n = view[cRead](offset, true);
          offset += cSize;
          if (prop === faceProp) {
            // Fan-triangulate polygons
            const first = view[iRead](offset, true);
            for (let k = 1; k + 1 < n; k++) {
              triangles.push(first, view[iRead](offset + k * iSize, true), view[iRead](offset + (k + 1) * iSize, true));
            }
          }
          offset += n * iSize;
        }
      }
      void countRead; void countSize; void indexRead; void indexSize;
      indices = Uint32Array.from(triangles);
    } else {
      throw new Error(`Unsupported PLY element "${element.name}"`);
    }
  }

  if (!positions || !indices) throw new Error('PLY file has no mesh faces');
  return { positions, indices };
};

const findHeaderEnd = (bytes) => {
  const marker = 'end_header';
  const limit = Math.min(bytes.length, 65536);
  for (let i = 0; i < limit; i++) {
    let match = true;
    for (let k = 0; k < marker.length; k++) {
      if (bytes[i + k] !== marker.charCodeAt(k)) { match = false; break; }
    }
    if (match) {
      let end = i + marker.length;
      if (bytes[end] === 13) end++;
      if (bytes[end] === 10) end++;
      return end;
    }
  }
  throw new Error('PLY header not found');
};

/**
 * Rasterize a mesh into an elevation grid. Each cell takes the LOWEST surface
 * hit at its center, which favours ground over overhanging vegetation.
 */
export const buildTerrainGrid = (mesh, cellSize) => {
  const { positions, indices } = mesh;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const originX = Math.floor(minX / cellSize) * cellSize;
  const originY = Math.ceil(maxY / cellSize) * cellSize;
  const cols = Math.ceil((maxX - originX) / cellSize) + 1;
  const rows = Math.ceil((originY - minY) / cellSize) + 1;
  const data = new Float32Array(cols * rows).fill(NaN);

  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t] * 3, b = indices[t + 1] * 3, c = indices[t + 2] * 3;
    // Grid space: u = column, v = row (row grows southward)
    const ua = (positions[a] - originX) / cellSize - 0.5, va = (originY - positions[a + 1]) / cellSize - 0.5;
    const ub = (positions[b] - originX) / cellSize - 0.5, vb = (originY - positions[b + 1]) / cellSize - 0.5;
    const uc = (positions[c] - originX) / cellSize - 0.5, vc = (originY - positions[c + 1]) / cellSize - 0.5;
    const det = (vb - vc) * (ua - uc) + (uc - ub) * (va - vc);
    if (Math.abs(det) < 1e-12) continue;
    const za = positions[a + 2], zb = positions[b + 2], zc = positions[c + 2];

    const c0 = Math.max(0, Math.ceil(Math.min(ua, ub, uc)));
    const c1 = Math.min(cols - 1, Math.floor(Math.max(ua, ub, uc)));
    const r0 = Math.max(0, Math.ceil(Math.min(va, vb, vc)));
    const r1 = Math.min(rows - 1, Math.floor(Math.max(va, vb, vc)));
    for (let r = r0; r <= r1; r++) {
      for (let col = c0; col <= c1; col++) {
        const w1 = ((vb - vc) * (col - uc) + (uc - ub) * (r - vc)) / det;
        const w2 = ((vc - va) * (col - uc) + (ua - uc) * (r - vc)) / det;
        const w3 = 1 - w1 - w2;
        if (w1 < -1e-9 || w2 < -1e-9 || w3 < -1e-9) continue;
        const z = w1 * za + w2 * zb + w3 * zc;
        const idx = r * cols + col;
        if (!(data[idx] <= z)) data[idx] = z;
      }
    }
  }

  fillSmallHoles(data, cols, rows);
  return { originX, originY, cellSize, cols, rows, data };
};

// Fill single-cell gaps between triangles from their neighbours
const fillSmallHoles = (data, cols, rows) => {
  const source = data.slice();
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      const idx = r * cols + c;
      if (!Number.isNaN(source[idx])) continue;
      let sum = 0, n = 0;
      for (const k of [idx - 1, idx + 1, idx - cols, idx + cols]) {
        if (!Number.isNaN(source[k])) { sum += source[k]; n++; }
      }
      if (n >= 3) data[idx] = sum / n;
    }
  }
};

/**
 * Elevation at a map coordinate (bilinear), or null outside coverage
 */
export const sampleTerrain = (terrain, x, y) => {
  const { originX, originY, cellSize, cols, rows, data } = terrain;
  const u = (x - originX) / cellSize - 0.5;
  const v = (originY - y) / cellSize - 0.5;
  const c = Math.floor(u), r = Math.floor(v);
  if (c < 0 || r < 0 || c >= cols - 1 || r >= rows - 1) return null;
  const fu = u - c, fv = v - r;
  const i = r * cols + c;
  const z = (data[i] * (1 - fu) + data[i + 1] * fu) * (1 - fv)
    + (data[i + cols] * (1 - fu) + data[i + cols + 1] * fu) * fv;
  return Number.isNaN(z) ? null : z;
};

// Project files are JSON, so the grid travels as base64
export const serializeTerrain = (terrain) => {
  if (!terrain) return null;
  const bytes = new Uint8Array(terrain.data.buffer, terrain.data.byteOffset, terrain.data.byteLength);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return { ...terrain, data: btoa(binary) };
};

export const deserializeTerrain = (saved) => {
  if (!saved?.data) return null;
  if (saved.data instanceof Float32Array) return saved;
  const binary = atob(saved.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { ...saved, data: new Float32Array(bytes.buffer) };
};
